import {
  Content,
  EmptyState,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles } from '@material-ui/core/styles';
import { RunPanel } from './RunPanel';
import { TemplateRuns } from './TemplateRuns';
import { STATUS_COLOUR, STATUS_LABEL, useMyRuns } from './useMyRuns';

const useStyles = makeStyles(theme => ({
  lead: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    marginBottom: 20,
    maxWidth: 640,
  },
  row: { boxShadow: 'none', border: '1px solid var(--ar-border-soft)' },
  summary: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' },
  target: { fontWeight: 600, fontSize: '0.9375rem' },
  meta: { color: theme.palette.text.secondary, fontSize: '0.8125rem' },
  spacer: { flex: 1 },
}));

/**
 * Everything this person has asked the platform for, and where it got to.
 *
 * # Why the portal shows this at all
 *
 * The portal's scope is day-0 requests: it gathers intent, Control executes.
 * That boundary is right, and it left a hole — somebody filled in a form, got a
 * link to a different product, and had no way to answer "did my thing happen?"
 * without leaving. This closes it without moving the boundary: the page is
 * read-only, and approving still happens in Control.
 *
 * # Why it is the caller's own runs
 *
 * Control's dashboard shows the whole estate, deliberately. This one answers a
 * narrower question, and the filter is on the ID token's own email server-side
 * rather than on a query parameter — a caller-supplied actor would make this
 * "read anybody's request history".
 */
export const RequestsPage = () => {
  const classes = useStyles();
  const { runs, error, needsIdentity, loading, signIn } = useMyRuns();

  return (
    <Content>
      <Typography className={classes.lead}>
        Everything you have asked the platform for. Requests open pull requests
        and run a plan, then wait for a DevOps approval — a request sitting at
        "Waiting for DevOps" is working exactly as intended, and nothing in the
        cloud has changed yet. Approving happens in{' '}
        <Link to="https://control.answerrocket.com">Control</Link>.
      </Typography>

      {loading && <Progress />}
      {error && <ResponseErrorPanel error={error} />}

      {needsIdentity && (
        <EmptyState
          missing="info"
          title="Sign in with Google to see your requests"
          description="This page shows the requests YOU made, so it needs your Google identity — the same one Control records as the requester."
          action={
            <Button
              variant="contained"
              color="primary"
              onClick={() => void signIn()}
            >
              Sign in with Google
            </Button>
          }
        />
      )}

      {runs?.length === 0 && (
        <EmptyState
          missing="content"
          title="You have not asked for anything yet"
          description="Deploy a service or request an environment, and this page will follow it from pull request to approval."
          action={<Link to="/create/templates">Start something</Link>}
        />
      )}

      {runs?.map(run => (
        <Accordion
          key={run.id}
          className={classes.row}
          elevation={0}
          // Children are not rendered until expanded, so a page of runs opens
          // one SSE connection rather than fifty.
          TransitionProps={{ unmountOnExit: true }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={classes.summary}>
              <span className={classes.target}>{run.target}</span>
              <span className={classes.meta}>
                {run.kind} · {run.env}
              </span>
              <span className={classes.spacer} />
              <Chip
                size="small"
                label={STATUS_LABEL[run.status] ?? run.status}
                style={{
                  color: STATUS_COLOUR[run.status],
                  borderColor: STATUS_COLOUR[run.status],
                }}
                variant="outlined"
              />
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <RunPanel runId={run.id} initial={run} />
          </AccordionDetails>
        </Accordion>
      ))}

      <TemplateRuns />
    </Content>
  );
};
